import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = axios.create({ baseURL: '/api/v1' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const CLASSIFICATION_OPTIONS = [
  { value: 'managed_portal', label: 'Managed Portal and Disbursement Partner' },
  { value: 'direct_api', label: 'Direct API and Technical Integrator' },
  { value: 'hybrid', label: 'Hybrid Partner' },
];
const MODE_OPTIONS = [
  { value: 'client_portal', label: 'Client Portal / Web GUI' },
  { value: 'csv_batch', label: 'Self-help CSV batch uploads' },
  { value: 'partner_api', label: 'Partner API' },
  { value: 'sftp', label: 'SFTP batch processing' },
];
const SERVICE_OPTIONS = [
  { value: 'wallet_disbursement', label: 'Managed wallet and portal disbursements' },
  { value: 'vas_passthrough', label: 'VAS pass-through distribution' },
  { value: 'outbound_rails', label: 'Regulated outbound payment rails' },
];

interface Selections {
  classification: string | null;
  integration_modes: string[];
  platform_services: string[];
}

export const CpaAgreementSetupCard: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [classification, setClassification] = useState('');
  const [modes, setModes] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get(`/disbursement-clients/${clientId}/agreement-selections`);
        const selections = data?.data?.cpaSelections as Selections | undefined;
        if (!cancelled && selections) {
          setClassification(selections.classification || '');
          setModes(selections.integration_modes || []);
          setServices(selections.platform_services || []);
        }
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        if (!cancelled) setError(axiosErr?.response?.data?.error || 'Could not load the agreement setup');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const toggle = (current: string[], value: string) => (
    current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
  );

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await API.post(`/disbursement-clients/${clientId}/agreement-selections`, {
        classification,
        integration_modes: modes,
        platform_services: services,
      });
      setSaved(true);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr?.response?.data?.error || 'Could not save the agreement setup');
    } finally {
      setSaving(false);
    }
  }

  const boxCls = 'h-4 w-4 shrink-0 rounded border border-gray-400 accent-[#86BE41]';

  return (
    <div className="mymoolah-card overflow-hidden">
      <div className="p-5 border-b border-gray-100">
        <h3 className="admin-text-heading text-lg">Agreement setup</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          You choose the partner type, integration, and services. They are written into the Channel Partner Agreement. The partner only reads and accepts.
        </p>
      </div>
      <div className="p-5 space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
          </div>
        )}
        {!loading && (
          <>
            <fieldset className="space-y-1">
              <legend className="text-xs font-semibold text-gray-500 uppercase mb-1">Partner classification</legend>
              {CLASSIFICATION_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="partner_classification"
                    className={boxCls}
                    checked={classification === option.value}
                    onChange={() => { setClassification(option.value); setSaved(false); }}
                  />
                  {option.label}
                </label>
              ))}
            </fieldset>
            <fieldset className="space-y-1">
              <legend className="text-xs font-semibold text-gray-500 uppercase mb-1">Integration modes</legend>
              {MODE_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    className={boxCls}
                    checked={modes.includes(option.value)}
                    onChange={() => { setModes((prev) => toggle(prev, option.value)); setSaved(false); }}
                  />
                  {option.label}
                </label>
              ))}
            </fieldset>
            <fieldset className="space-y-1">
              <legend className="text-xs font-semibold text-gray-500 uppercase mb-1">Platform services</legend>
              {SERVICE_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    className={boxCls}
                    checked={services.includes(option.value)}
                    onChange={() => { setServices((prev) => toggle(prev, option.value)); setSaved(false); }}
                  />
                  {option.label}
                </label>
              ))}
            </fieldset>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {saved && <p className="text-sm text-emerald-700">Saved. The partner can now accept the agreement.</p>}
            <button
              type="button"
              onClick={save}
              disabled={saving || !classification || modes.length === 0 || services.length === 0}
              className="inline-flex items-center gap-1 rounded-xl bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : 'Save agreement setup'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
